import {
  Entity,
  EntityStringId,
  IEntity,
  EntityAssociations,
  EntityFields,
  IEntityType,
  IEntityTypeFor,
  ExtractIEntity,
  IIdentifiableEntity,
} from 'app/types/entity';
import { AppState } from 'app/store';
import { notNull } from 'app/utils/schema';
import * as Schema from 'app/constants/schemas';
import { getEntityOfTypeOrThrow } from 'app/models/entities/selectors/next';
import { IWord } from 'app/constants/schemas/Word';
import { ILocalWord } from 'app/constants/schemas/LocalWord';
import { ILocalSentence } from 'app/constants/schemas/LocalSentence';
import { IWordSentence } from 'app/constants/schemas/WordSentence';
import { ISentence } from 'app/constants/schemas/Sentence';
import { IBibliography } from 'app/constants/schemas/Bibliography';
import { ILocalBibliography } from 'app/constants/schemas/LocalBibliography';

interface IFieldInfo <
  Schema extends IEntity,
  Field extends keyof EntityFields<Schema>,
> {
  id?: Entity<Schema>[Field] | null;
  type?: IEntityType<Schema>;
  field: keyof EntityFields<Schema> | null;
  available: boolean;
  selected: boolean;
  notNull: boolean;
}

/**
 * Helper for determining information about a field in an entity.
 */
export const getFieldInfo = <
  Schema extends IEntity,
  Field extends keyof EntityFields<Schema>,
>(
  entity: Entity<Schema> | undefined,
  fieldName: Field | null,
) => {
  const id = fieldName && entity && entity[fieldName];
  const info: IFieldInfo<Schema, Field> = {
    id,
    field: fieldName,
    type: entity && entity.schema,
    available: !!fieldName,
    selected: !!id,
    notNull: Boolean(entity && fieldName && notNull(entity, fieldName)),
  };
  return info;
};

// A helper that just throws when none of the unioned fields had a value.
const defaultHandlerThrowError = (_: unknown) => {
  throw new Error('None of the specified unioned fields had a value');
};

/**
 * Create a vistor for an entity with 2 unioned fields.
 * @param type the type constructor for the entity containing the union
 * @param fields a list of the fields that are unioned
 * @returns visitor a function that takes the store, parent id, and handlers for each of the
 * union cases.
 */
export function makeUnionEntityVisitor<
  T extends IEntity,
  K1 extends Extract<keyof T['associations'], string>,
  K2 extends Extract<Exclude<keyof T['associations'], K1>, string>,
>(
  type: IEntityType<T>,
  fields: [K1, K2],
):
  <Return>(
    state: AppState,
    id: EntityStringId,
    handlers: {
      [K in K1 | K2]: (
        value: Entity<ExtractIEntity<IEntityTypeFor<T['associations'][K]>>>
      ) => Return
    } & {
      defaultHandler?: (x: Entity<T>) => Return
    },
  ) => Return;
/**
 * Create a vistor for an entity with 3 unioned fields.
 * @param type the type constructor for the entity containing the union
 * @param fields a list of the fields that are unioned
 * @returns visitor a function that takes the store, parent id, and handlers for each of the
 * union cases.
 */
export function makeUnionEntityVisitor<
  T extends IEntity,
  K1 extends Extract<keyof T['associations'], string>,
  K2 extends Extract<Exclude<keyof T['associations'], K1>, string>,
  K3 extends Extract<Exclude<keyof T['associations'], K1 | K2>, string>,
>(
  type: IEntityType<T>,
  fields: [K1, K2, K3],
):
  <Return>(
    state: AppState,
    id: EntityStringId,
    handlers: {
      [K in K1 | K2 | K3]: (
        value: Entity<ExtractIEntity<IEntityTypeFor<T['associations'][K]>>>
      ) => Return
    } & {
      defaultHandler?: (x: Entity<T>) => Return
    },
  ) => Return;
/**
 * Create a vistor for an entity with unioned fields.
 * @param type the type constructor for the entity containing the union
 * @param fields a list of the fields that are unioned
 * @returns visitor a function that takes the store, parent id, and handlers for each of the
 * union cases.
 */
export function makeUnionEntityVisitor<
  T extends IIdentifiableEntity,
  K extends Extract<keyof T['associations'], string>
>(
  type: IEntityType<T>,
  fields: K[],
) {
  return <U>(
    state: AppState,
    id: T['attributes']['id'],
    handlers: {
      [Field in keyof T['associations']]: (value: Entity<ExtractIEntity<IEntityTypeFor<T['associations'][Field]>>>) => U
    } & {
      defaultHandler?: (x: Entity<T>) => U;
    },
  ) => {
    const entity = getEntityOfTypeOrThrow(state, type, id);
    const unionedFields = fields.map((key) => ({
      key,
      // we know that this is an association because K is already restricted by
      // the generic. The undefined is needed otherwise it fails to cast
      // cleanly
      value: entity.get(key) as undefined | EntityAssociations<T>[K],
    }));

    for (const { key, value } of unionedFields) {
      // value is possibly an array if it's a collection but that doesn't seem
      // to fail typechecking
      if (value !== undefined && value !== null && !Array.isArray(value)) {
        const fieldType = type.associations[key].type;

        // we've already checked that value is not undefined and not null but
        // this doesn't seem to be enough for typescript and we have to assert
        const field = getEntityOfTypeOrThrow(state, fieldType, value!);
        return handlers[key](field);
      }
    }
    return handlers.defaultHandler ? handlers.defaultHandler(entity) : defaultHandlerThrowError(entity);
  };
}

/**
 * Visit a VocabSectionWord's word and localWord fields.
 * @params state the redux state.
 * @params id the id of the vocab section word.
 * @params handlers handler functions for word, local word, and optionally a base case when neither is set.
 * @returns the return value of the handler functions.
 */
export const visitVocabSectionWord = makeUnionEntityVisitor(
  Schema.VocabSectionWord,
  ['localWord', 'word'],
);

/**
 * A helper for just getting the word or local word out of a vocab section word.
 * @param state the redux state.
 * @param id the vocab section word id to unpack.
 * @returns word the word or local word in vocab section word.
 */
export const unpackVocabSectionWordUnion = (state: AppState, id: EntityStringId) =>
  visitVocabSectionWord(state, id, {
    word: (w: Entity<IWord> | Entity<ILocalWord>) => w,
    localWord: (l) => l,
  });

/**
 * Visit a VocabSectionWordSentence's globalSentence, localSentence, or wordSentence fields.
 * @params state the redux state.
 * @params id the id of the vocab section word sentence.
 * @params handlers handler functions for global sentence, local sentence, word sentence,
 *         and optionally a base case when neither is set.
 * @returns the return value of the handler functions.
 */
export const visitVocabSectionWordSentence = makeUnionEntityVisitor(
  Schema.VocabSectionWordSentence,
  ['localSentence', 'wordSentence', 'globalSentence'],
);

/**
 * A helper for just getting the sentence out of the vocab section word sentence.
 * @param state the redux state.
 * @param id the vocab section word sentence id to unpack.
 * @returns word the globalSentence, localSentence, or word sentence in vocab section word sentence.
 */
export const unpackVocabSectionWordSentenceUnion = (state: AppState, id: EntityStringId) =>
  visitVocabSectionWordSentence(state, id, {
    globalSentence: (g: Entity<ISentence> | Entity<ILocalSentence> | Entity<IWordSentence>) => g,
    localSentence: (l) => l,
    wordSentence: (w) => w,
  });

/**
 * Visit an AttributionBibliography's bibliography and localBibliography fields.
 * @param state redux state
 * @param id AttributionBibliography id
 * @param handlers handler functions for bibliography, local bibliography, and optionally a base
 *   case when neither is set
 * @returns return value of the handler functions
 */
export const visitLessonBibliography = makeUnionEntityVisitor(
  Schema.LessonBibliography,
  ['bibliography', 'localBibliography'],
);

/**
 * Helper for getting the bibliography or local bibliography out of an AttributionBibliography
 * @param state redux state
 * @param id AttributionBibliography id
 * @returns bibliography or local bibliography in AttributionBibliography
 */
export const unpackLessonBibliography = (state: AppState, id: EntityStringId) =>
  visitLessonBibliography(state, id, {
    bibliography: (b: Entity<IBibliography> | Entity<ILocalBibliography>) => b,
    localBibliography: (l) => l,
  });
